import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  type InitiateAuthCommandOutput,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import {
  config,
  setCognitoStorage,
} from "@mobsuccess-devops/react-shared/auth";
import { axiosPost } from "@mobsuccess-devops/react-shared/fetch";
import {
  startAuthentication,
  type AuthenticatorTransport,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

import {
  fetchPasskeyGeo,
  formatPasskeyDeviceLabel,
} from "@/features/passkeys/passkey-device-label";

export class PasskeySignInAbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PasskeySignInAbortedError";
  }
}

/**
 * Thrown when the backend reports that passkey sign-in cannot proceed for this
 * email (no passkey registered for the account, or the account is unknown). The
 * CreateAuthChallenge Lambda rejects the `InitiateAuth` call before any WebAuthn
 * prompt is shown, so callers can silently fall back to password sign-in.
 */
export class PasskeyNotRegisteredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PasskeyNotRegisteredError";
  }
}

function cognitoRegionFromUserPoolId(userPoolId: string): string {
  const i = userPoolId.indexOf("_");
  if (i <= 0) {
    return "eu-central-1";
  }
  return userPoolId.slice(0, i);
}

type SignInWithPasskeyParams = {
  email: string;
  userPoolClientId: string;
  userPoolId: string;
  userPoolsWebClientId: string;
};

export async function signInWithPasskey({
  email,
  userPoolClientId,
  userPoolId,
  userPoolsWebClientId,
}: SignInWithPasskeyParams): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    throw new Error("Email required");
  }
  if (!window.PublicKeyCredential) {
    throw new Error("Passkeys not supported");
  }
  if (window.self !== window.top) {
    if (window.top) {
      window.top.location.href = window.location.href;
    }
    throw new PasskeySignInAbortedError("iframe-redirect");
  }

  await axiosPost({
    entity: "auth",
    verb: "storePasskeyAuthType",
    token: null,
    body: { email: normalized, authType: "PASSKEY" },
    endpoint: config.msApiEndpointApp,
  });

  const client = new CognitoIdentityProviderClient({
    region: cognitoRegionFromUserPoolId(userPoolId),
  });

  let init: InitiateAuthCommandOutput;
  try {
    init = await client.send(
      new InitiateAuthCommand({
        AuthFlow: "CUSTOM_AUTH",
        ClientId: userPoolClientId,
        AuthParameters: { USERNAME: normalized },
        ClientMetadata: { authType: "PASSKEY" },
      }),
    );
  } catch (error) {
    // Cognito signals "this account can't sign in with a passkey" before any
    // WebAuthn prompt in two ways: the CreateAuthChallenge Lambda rejects a
    // known user that has no passkey (UserLambdaValidationException), and an
    // unknown account short-circuits as UserNotFoundException. Both should fall
    // back to the password step instead of surfacing as an error.
    if (
      error instanceof Error &&
      (error.name === "UserLambdaValidationException" ||
        error.name === "UserNotFoundException")
    ) {
      throw new PasskeyNotRegisteredError(error.message);
    }
    throw error;
  }

  if (
    init.ChallengeName !== "CUSTOM_CHALLENGE" ||
    !init.ChallengeParameters?.challenge ||
    !init.Session
  ) {
    throw new Error("Unexpected passkey challenge from Cognito");
  }

  const { challenge, allowCredentials } = init.ChallengeParameters;
  type AllowCred = {
    id: string;
    transports?: AuthenticatorTransport[];
  };
  let allowCreds: AllowCred[] = [];
  try {
    const parsed: AllowCred[] = JSON.parse(allowCredentials || "[]");
    allowCreds = parsed;
  } catch {
    throw new Error("Invalid allowCredentials from Cognito");
  }

  const optionsJSON: PublicKeyCredentialRequestOptionsJSON = {
    challenge,
    ...(allowCreds.length > 0
      ? {
          allowCredentials: allowCreds.map((c) => ({
            id: c.id,
            type: "public-key",
            ...(c.transports?.length ? { transports: c.transports } : {}),
          })),
        }
      : {}),
    rpId: window.location.hostname,
    timeout: 60_000,
    userVerification: "preferred",
  };

  const geoPromise = fetchPasskeyGeo();
  const credential = await startAuthentication({ optionsJSON });
  const geo = await geoPromise;

  const resp = await client.send(
    new RespondToAuthChallengeCommand({
      ChallengeName: "CUSTOM_CHALLENGE",
      ClientId: userPoolClientId,
      Session: init.Session,
      ChallengeResponses: {
        USERNAME: normalized,
        ANSWER: JSON.stringify(credential),
      },
      ClientMetadata: {
        authType: "PASSKEY",
        deviceInfo: formatPasskeyDeviceLabel(geo),
      },
    }),
  );

  const auth = resp.AuthenticationResult;
  if (!auth?.AccessToken || !auth.RefreshToken || !auth.IdToken) {
    throw new Error("Incomplete authentication result");
  }

  await setCognitoStorage(
    userPoolsWebClientId,
    auth.AccessToken,
    auth.RefreshToken,
    auth.IdToken,
    normalized,
  );

  await axiosPost({
    entity: "auth",
    verb: "login",
    token: `Bearer jwt:${auth.AccessToken}`,
  });
}
